import { FC, useEffect, useMemo, useState } from 'react';
import { CheckboxTickIcon, CustomersTabIcon, NavCloseIcon, ThinSearchIcon } from 'assets/icons';
import CustomAvatar from 'components/avatar/CustomAvatar';
import AssignableMembersModal from 'containers/jobs/create-edit-job/components/AssignableMembersModal';
import { AssignableMember } from 'containers/jobs/jobs.model';
import { useAppSelector } from 'hooks';
import { getInitials } from 'utils/commonUtils';
import OutsideClickHandle from 'components/outside-click-handler/OutsideClickHandler';
import { selectUserDetails } from 'containers/app/appSlice';
import CustomInput from 'components/custom-input.tsx/CustomInput';
import { CustomCheckbox } from 'components';

interface EnhancedAssignableMember extends AssignableMember {
  isSelected?: boolean;
}

interface AssignTeamMembersProps {
  onMembersChange: (members: AssignableMember[]) => void;
  membersList?: AssignableMember[];
  initialSelectedMembers?: AssignableMember[];
  onOpenModal: () => void;
  isLoading?: boolean;
  lockedMemberId?: string | null;
}

const AssignTeamMembers: FC<AssignTeamMembersProps> = ({
  onMembersChange,
  membersList = [],
  initialSelectedMembers = [],
  onOpenModal,
  isLoading = false,
  lockedMemberId = null
}) => {
  const [searchText, setSearchText] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [assigneeList, setAssigneeList] = useState<AssignableMember[]>([]);
  const [suggestionList, setSuggestionList] = useState<EnhancedAssignableMember[]>([]);
  const [openAssignMembersModal, setOpenAssignMembersModal] = useState(false);
  const currentUser = useAppSelector(selectUserDetails);

  // Memoize props to prevent unnecessary re-renders
  const memoizedInitialMembers = useMemo(
    () => initialSelectedMembers,
    [JSON.stringify(initialSelectedMembers?.map(m => m.merchantId) || [])]
  );

  const memoizedMembersList = useMemo(() => membersList, [JSON.stringify(membersList?.map(m => m.merchantId) || [])]);

  // Initialize with initial selected members
  useEffect(() => {
    if (memoizedInitialMembers && memoizedInitialMembers.length > 0) {
      setAssigneeList(memoizedInitialMembers);
    } else {
      setAssigneeList([]);
    }
  }, [memoizedInitialMembers]);

  useEffect(() => {
    if (memoizedMembersList.length > 0) {
      // Mark initially selected members
      const updatedSuggestionList = memoizedMembersList.map(member => ({
        ...member,
        isSelected: assigneeList.some(assignee => assignee.merchantId === member.merchantId)
      }));
      setSuggestionList(updatedSuggestionList);
    }
  }, [memoizedMembersList, assigneeList]);

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    if (value.trim()) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleMemberSelection = (member: AssignableMember) => {
    // Toggle selection
    const isCurrentlySelected = assigneeList.some(assignee => assignee.merchantId === member.merchantId);

    const newAssigneeList = isCurrentlySelected
      ? assigneeList.filter(assignee => assignee.merchantId !== member.merchantId)
      : [...assigneeList, member];

    setAssigneeList(newAssigneeList);
    onMembersChange(newAssigneeList);

    // Update suggestion list selection state
    setSuggestionList(
      suggestionList.map(
        item => (item.merchantId === member.merchantId ? { ...item, isSelected: !isCurrentlySelected } : item)
        // eslint-disable-next-line function-paren-newline
      )
    );
    setSearchText('');
  };

  const removeFromAssigneeList = (merchantId: string) => {
    // Don't allow removing locked member
    if (lockedMemberId === merchantId) {
      return;
    }
    const newAssigneeList = assigneeList.filter(member => member.merchantId !== merchantId);
    setAssigneeList(newAssigneeList);
    onMembersChange(newAssigneeList);

    // Update suggestion list selection state
    setSuggestionList(
      suggestionList.map(item => (item.merchantId === merchantId ? { ...item, isSelected: false } : item))
    );
  };

  const getFilteredSuggestions = () => {
    return suggestionList.filter(member => member.name.toLowerCase().includes(searchText.toLowerCase()));
  };

  const renderSuggestions = () => {
    const filteredList = getFilteredSuggestions();

    return filteredList.length > 0 ? (
      filteredList.map(member => {
        const isLocked = member.merchantId === lockedMemberId;
        const isSelected = member.isSelected || assigneeList.some(m => m.merchantId === member.merchantId);
        return (
          <div
            key={member.merchantId}
            className={`flex cursor-pointer items-center gap-5 
            border-b border-borderGray py-3 px-1 last:border-b-0 hover:bg-highlightGray
            ${isLocked && isSelected ? 'cursor-not-allowed opacity-70' : ''}`}
            onClick={e => {
              e.stopPropagation();
              if (!isLocked || !isSelected) {
                handleMemberSelection(member);
              }
            }}>
            <CustomCheckbox
              isChecked={isSelected}
              onClick={() => {
                if (!isLocked || !isSelected) {
                  handleMemberSelection(member);
                }
              }}
              id={`member-${member.merchantId}`}
              disabled={isLocked && isSelected}>
              {isSelected && <CheckboxTickIcon />}
            </CustomCheckbox>
            <CustomAvatar src={member.profilePictureUrl}>{getInitials(member.name)}</CustomAvatar>
            <div className="text-lg font-semibold leading-5 text-headingGray">{member.name}</div>
            <div className="text-accentText">{member.phone}</div>
          </div>
        );
      })
    ) : (
      <div className="mx-auto my-5 text-sbase font-semibold text-primaryText">No results found!</div>
    );
  };

  const renderAssigneeList = () => {
    return assigneeList.map(member => (
      <div key={member.merchantId} className="flex items-center gap-2 rounded-[10px] bg-borderGray py-1 px-2">
        <div className="text-sbase text-primaryText">
          {member.name}
          {member.merchantId === currentUser?.id && <span> {'(you)'}</span>}
        </div>
        {lockedMemberId !== member.merchantId && (
          <NavCloseIcon
            className="path-fill-current path cursor-pointer text-gray-1"
            width={10}
            height={10}
            onClick={() => removeFromAssigneeList(member.merchantId)}
          />
        )}
      </div>
    ));
  };

  const handleCloseAssignMembersModal = () => {
    setOpenAssignMembersModal(false);
  };

  const handleSave = (selectedMembers: AssignableMember[]) => {
    setAssigneeList(selectedMembers);
    setSuggestionList(
      suggestionList.map(item => ({
        ...item,
        isSelected: selectedMembers.some(member => member.merchantId === item.merchantId)
      }))
    );

    handleCloseAssignMembersModal();
  };

  return (
    <div className="w-full">
      <div className="flex items-start justify-between rounded bg-secondaryBtn px-5 py-2.5">
        <div className="relative flex grow items-start">
          <div className="mr-2 flex h-[26px] items-center justify-center">
            <ThinSearchIcon />
          </div>
          <div className="flex flex-wrap gap-2">
            {renderAssigneeList()}
            <CustomInput
              id="search-member"
              value={searchText}
              onChange={handleSearchChange}
              placeholder={assigneeList.length > 0 ? 'Search another member' : 'Search a member'}
              className="h-6 p-0 outline-none"
            />
          </div>

          {showSuggestions && (
            <OutsideClickHandle onOutsideClick={() => setShowSuggestions(false)}>
              <div
                className="absolute top-10 -left-5 z-20 w-full 
                rounded border border-borderGray bg-primary px-3 py-2 shadow-md"
                style={{
                  boxShadow: 'rgba(0, 0, 0, 0.25)'
                }}>
                {renderSuggestions()}
              </div>
            </OutsideClickHandle>
          )}
        </div>

        <div className="flex shrink-0 cursor-pointer items-center gap-2" role="presentation" onClick={onOpenModal}>
          <CustomersTabIcon
            className="path-stroke-current path-fill-current path text-secondary"
            width={24}
            height={24}
          />
          <div className="text-17px font-semibold text-secondary">View all members</div>
        </div>
      </div>

      {openAssignMembersModal && (
        <AssignableMembersModal
          list={suggestionList}
          onClose={handleCloseAssignMembersModal}
          selectedMembers={assigneeList}
          onSave={handleSave}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default AssignTeamMembers;
